import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Search, Filter, Package, ChevronRight, ArrowLeft } from 'lucide-react';
import MarketplaceLayout from '../../layouts/MarketplaceLayout';

export default function SearchResults() {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [categories, setCategories] = useState(['all']);
    const [currentUser, setCurrentUser] = useState(null);
    const navigate = useNavigate();

    const query = searchParams.get('q') || '';

    useEffect(() => {
        fetchCurrentUser();
    }, []);

    useEffect(() => {
        if (query) {
            setSearchTerm(query);
            fetchSearchResults(query);
        }
    }, [query]);

    const fetchCurrentUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await api.get('/api/user/profile');
            setCurrentUser(response.data);
        } catch (error) {
            console.log('User not authenticated');
        }
    };

    const fetchSearchResults = async (searchQuery) => {
        try {
            setLoading(true);
            const response = await api.get(`/api/marketplace/apps?search=${encodeURIComponent(searchQuery)}`);

            const appsWithUserInfo = response.data.map(app => ({
                ...app,
                user_id: app.user_id
            }));

            setApps(appsWithUserInfo);

            // Extract unique categories from results
            const allCategories = appsWithUserInfo
                .map(app => app.category)
                .filter(Boolean);
            const uniqueCategories = ['all', ...new Set(allCategories)];
            setCategories(uniqueCategories);
        } catch (error) {
            console.error('Failed to fetch search results:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            setSearchParams({ q: searchTerm.trim() });
        }
    };

    const handleAppClick = (app) => {
        const identifier = app.slug || app.id;

        if (currentUser && app.user_id === currentUser.id) {
            navigate(`/dashboard/apps`);
        } else {
            navigate(`/marketplace/app/${identifier}`);
        }
    };

    const filteredApps = selectedCategory === 'all'
        ? apps
        : apps.filter(app => app.category === selectedCategory);

    if (loading) {
        return (
            <MarketplaceLayout>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </MarketplaceLayout>
        );
    }

    return (
        <MarketplaceLayout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back Button */}
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Back to Marketplace
                </Link>

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Search Results
                    </h1>
                    <p className="text-gray-600">
                        {filteredApps.length} {filteredApps.length === 1 ? 'result' : 'results'} found for "{query}"
                    </p>
                </div>

                {/* Search Bar */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-8">
                    <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Refine your search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Filter className="text-gray-400 w-5 h-5" />
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>
                                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="submit"
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Search
                        </button>
                    </form>
                </div>

                {/* Results */}
                <div>
                    {filteredApps.length === 0 ? (
                        <div className="text-center py-12">
                            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                No apps found
                            </h3>
                            <p className="text-gray-600 mb-6">
                                We couldn't find any apps matching "{query}"
                                {selectedCategory !== 'all' && ` in category "${selectedCategory}"`}
                            </p>
                            <Link
                                to="/marketplace/categories"
                                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Browse All Categories
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredApps.map(app => (
                                <AppCard
                                    key={app.id}
                                    app={app}
                                    onClick={() => handleAppClick(app)}
                                    isOwnApp={currentUser && app.user_id === currentUser.id}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </MarketplaceLayout>
    );
}

// App Card Component
const AppCard = ({ app, onClick, isOwnApp }) => {
    return (
        <div
            onClick={onClick}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-all cursor-pointer group"
        >
            <div className="flex items-start justify-between mb-4">
                {app.icon_url ? (
                    <img
                        src={app.icon_url}
                        alt={app.name}
                        className="w-14 h-14 rounded-lg object-cover"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                        }}
                    />
                ) : null}
                <div
                    className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center"
                    style={{ display: app.icon_url ? 'none' : 'flex' }}
                >
                    <Package className="w-7 h-7 text-gray-500" />
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">{app.name}</h3>
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {app.short_description || app.description}
            </p>

            {app.category && (
                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                    {app.category}
                </span>
            )}

            <div className="flex items-center justify-between text-sm mt-4">
                <div className="font-semibold text-gray-900">
                    {isOwnApp ? (
                        <span className="text-gray-500">Your App</span>
                    ) : (
                        app.pricing_model === 'free' ? (
                            <span className="text-green-600">Free</span>
                        ) : (
                            <span>${app.price} {app.price_period && `/ ${app.price_period}`}</span>
                        )
                    )}
                </div>
                <span className="text-blue-600 group-hover:underline">
                    {isOwnApp ? 'Manage →' : 'View Details →'}
                </span>
            </div>
        </div>
    );
};
